import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, type DropdownItemProps, Icon } from "semantic-ui-react";
import { DropdownWrapper, FilterSidebar, TextTruncate } from "../../../../../components";
import { type DateRange } from "../../types/performance";
import { Tooltip } from "components/common/tooltip";

import "./FlowPerformanceFilter.scss";
import { isEqual } from "lodash";
import {
  allCustomerAccounts,
  customDropdownSearch,
  mappedPriorityLevels,
  mappedStatusLevels,
  type Accounts,
} from "features/Library/Common/utils/performanceUtils";
import { FilterMultiSelectWithWindow } from "components/multiSelect/FilterMultiSelectWithWindow";
import { FilterMultiSelect } from "components/multiSelect";
import { useReportingAccountsList, useReportingAccountType } from "components/reportingFilter/ReportingFilterRequests";
import { useAddOnQuery } from "features/Reporting/Content/queries/useAddOnQuery";
import { AddOnIds } from "enums";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { type AccountType } from "../../../../../interfaces/Account";

type VersionFilter = {
  date: number;
  type: AccountType;
  isDistinct: boolean;
  includeOwnData?: boolean;
  accounts?: any[];
  versionNumber: number;
  priority: number[];
  status: number[];
};

// All the +1/-1 requirements are because when a number is 0 the FilterSidebar's
// change handler returns undefined instead. So counting instead goes 1,2,...
const FilterToSidebarFilter = (num: number) => num + 1;
const SidebarFilterToFilter = (num: number) => num - 1;

export interface Props {
  dateRanges: DateRange[];
  currentFilter: {
    date: number;
    includeOwnData?: boolean;
    accounts?: any[];
    type: AccountType;
    isDistinct: boolean;
    versionNumber: number;
    priority: number[];
    status: number[];
  };
  setCurrentFilter: (filter: {
    date: number;
    includeOwnData?: boolean;
    accounts?: any[];
    type: AccountType;
    isDistinct: boolean;
    versionNumber: number;
    priority: number[];
    status: number[];
  }) => void;
  includeAccountsDropdown: boolean;
  handleReset: () => void;
  disabled?: boolean;
  accountId: number;
  accountName: string;
  publisherId: number;
}

export const FlowFilter: FC<Props> = ({
  currentFilter,
  dateRanges,
  setCurrentFilter,
  handleReset,
  includeAccountsDropdown,
  accountId,
  accountName,
  disabled = false,
  publisherId,
}) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const addOnQuery = useAddOnQuery(accountId);
  const type = useReportingAccountType(accountId, true, publisherId);
  const shouldSeeDropdown = useMemo(() => {
    const addOns = addOnQuery.data ?? [];
    const hasManageAccountsAddOn = addOns.some((addon) => addon.id === AddOnIds.CreateAccounts && addon.isEnabled);
    const hasMarketplaceAddOn = addOns.some((addon) => addon.id === AddOnIds.Marketplace && addon.isEnabled);
    return hasManageAccountsAddOn && hasMarketplaceAddOn && type !== "bsi";
  }, [addOnQuery.data, type]);

  const [currentBoxFilter, setCurrentBoxFilter] = useState({
    date: FilterToSidebarFilter(currentFilter.date),
    ...(includeAccountsDropdown && { includeOwnData: currentFilter.includeOwnData }),
    ...(includeAccountsDropdown && { accounts: currentFilter.accounts }),
    isDistinct: currentFilter.isDistinct ?? false,
    type: currentFilter.type,
    versionNumber: currentFilter.versionNumber,
    priority: currentFilter.priority,
    status: currentFilter.status,
  });
  const [visible, setVisible] = useState(false);

  const createDateString = useCallback(
    (range?: DateRange): string => {
      if (!range) return "";
      const stringStartDate = formatDateTime(range.DATETIME_START);
      const stringEndDate = range.DATETIME_END !== null ? formatDateTime(range.DATETIME_END) : "Present";

      return `${stringStartDate} - ${stringEndDate}`;
    },
    [formatDateTime],
  );

  useEffect(() => {
    setCurrentBoxFilter({
      date: FilterToSidebarFilter(currentFilter.date),
      ...(includeAccountsDropdown && { includeOwnData: currentFilter.includeOwnData }),
      ...(includeAccountsDropdown && { accounts: currentFilter.accounts }),
      isDistinct: currentFilter.isDistinct ?? false,
      type: currentFilter.type,
      versionNumber: currentFilter.versionNumber,
      priority: currentFilter.priority,
      status: currentFilter.status,
    });
  }, [currentFilter, includeAccountsDropdown]);

  const defaultFilter = useMemo<VersionFilter>(
    () => ({
      date: dateRanges.length,
      ...(includeAccountsDropdown && { includeOwnData: true }),
      ...(includeAccountsDropdown && { accounts: ["All Customer Accounts"] }),
      isDistinct: false,
      type: type ?? "account",
      versionNumber: currentFilter.versionNumber,
      priority: [],
      status: [],
    }),
    [dateRanges.length, includeAccountsDropdown, type, currentFilter.versionNumber],
  );

  const updateFilter = useCallback((newFilter: VersionFilter) => {
    setCurrentBoxFilter(newFilter);
  }, []);

  const handleSubmit = useCallback(
    (newFilter: VersionFilter) => {
      setCurrentFilter({
        date: SidebarFilterToFilter(newFilter.date),
        ...(includeAccountsDropdown && { includeOwnData: newFilter.includeOwnData }),
        ...(includeAccountsDropdown && {
          accounts: newFilter.accounts?.map((account: any) => {
            if (typeof account === "string" || typeof account === "number") return account;
            return account.value;
          }),
        }),
        isDistinct: newFilter.isDistinct,
        type: newFilter.type,
        versionNumber: newFilter.versionNumber,
        priority: newFilter.priority,
        status: newFilter.status,
      });
      setVisible(false);
    },
    [setCurrentFilter, includeAccountsDropdown],
  );

  const handleLocalReset = useCallback(() => {
    setCurrentBoxFilter({
      date: dateRanges.length,
      ...(includeAccountsDropdown && { includeOwnData: true }),
      ...(includeAccountsDropdown && { accounts: ["All Customer Accounts"] }),
      isDistinct: false,
      type: currentFilter.type,
      versionNumber: currentFilter.versionNumber,
      priority: [],
      status: [],
    });
    handleReset();
  }, [dateRanges.length, handleReset, includeAccountsDropdown, currentFilter.type, currentFilter.versionNumber]);

  const mappedRanges = useMemo<DropdownItemProps[]>(() => {
    return dateRanges.map<DropdownItemProps>((v, i) => ({
      key: i,
      value: i + 1,
      text: createDateString(v),
    }));
  }, [dateRanges, createDateString]);

  const shouldApplyFilterBeDisabled = useMemo<boolean>(() => {
    // The + 1 is due to currentFilter doing - 1 within FlowPerformance, to get the correct index.
    if (isEqual(currentBoxFilter, { ...currentFilter, date: currentFilter.date + 1 })) return true;
    return false;
  }, [currentBoxFilter, currentFilter]);

  return (
    <div className="flowPerformanceFilter">
      <FilterSidebar
        visible={visible}
        hideFilter={() => setVisible(false)}
        resetFilter={handleLocalReset}
        applyFilter={handleSubmit}
        appliedFilter={{
          date: FilterToSidebarFilter(currentFilter.date),
          ...(includeAccountsDropdown && { includeOwnData: currentFilter.includeOwnData }),
          ...(includeAccountsDropdown && { accounts: currentFilter.accounts }),
          isDistinct: currentFilter.isDistinct,
          type: currentFilter.type,
          versionNumber: currentFilter.versionNumber,
          priority: currentFilter.priority,
          status: currentFilter.status,
        }}
        defaultFilter={defaultFilter}
        shouldDisableApply={shouldApplyFilterBeDisabled}
        filterOptions={mappedRanges}
        getFilterOptions={() => mappedRanges}
      >
        <LocalDropdownFilter
          currentBoxFilter={currentBoxFilter}
          updateLocalFilter={updateFilter}
          includeAccountsDropdown={includeAccountsDropdown}
          filterOptions={dateRanges}
          accountName={accountName}
          shouldSeeDropdown={shouldSeeDropdown}
          accountId={accountId}
        />
      </FilterSidebar>
      {currentFilter.date >= 0 && (
        <div className="chip">
          <span>Date: </span>
          {createDateString(dateRanges[currentFilter.date])}
        </div>
      )}
      <button
        data-testid="flowFilterVisibilityToggle"
        className="flowFilterButton"
        onClick={() => setVisible((v) => !v)}
        type="button"
        disabled={!dateRanges.length || disabled}
      >
        <Icon name="filter" fitted /> Filter
      </button>
    </div>
  );
};

interface FiltersFromSidebarParent {
  updateFilter?: (...args: any[]) => void;
}
interface CustomDropdownProps extends FiltersFromSidebarParent {
  currentBoxFilter: {
    date: number;
    type: AccountType;
    includeOwnData?: boolean;
    accounts?: any[];
    isDistinct: boolean;
    versionNumber: number;
    priority: number[];
    status: number[];
  };
  updateLocalFilter: (newIndex: VersionFilter) => void;
  filterOptions: DropdownItemProps[];
  shouldSeeDropdown?: boolean;
  includeAccountsDropdown: boolean;
  accountName?: string;
  accountId: number;
  priority?: number[];
  status?: number[];
}

const LocalDropdownFilter: FC<CustomDropdownProps> = ({
  currentBoxFilter,
  filterOptions,
  updateLocalFilter,
  updateFilter,
  includeAccountsDropdown,
  accountName,
  shouldSeeDropdown,
  accountId,
}) => {
  // Add accounts hook and search term state
  const [accountsSearchTerm, setAccountsSearchTerm] = useState("");
  const reportingAccountsWithSearch = useReportingAccountsList(currentBoxFilter.type, accountId, {
    top: 100,
    term: accountsSearchTerm,
    accumulate: true,
  });
  const mappedDistinct = [
    { key: 0, value: false, text: "Total" },
    { key: 1, value: true, text: "Distinct" },
  ];

  const handleChange = useCallback(
    (
      data: any,
      type: "date" | "includeOwnData" | "accountsDropdown" | "isDistinct" | "type" | "priority" | "status",
    ) => {
      if (!updateFilter || !updateLocalFilter) return;
      if (type === "date") {
        updateFilter({
          ...currentBoxFilter,
          date: data.value,
          versionNumber: data.value,
        });
        updateLocalFilter({
          ...currentBoxFilter,
          date: data.value as number,
          versionNumber: data.value as number,
        });
      }
      if (type === "includeOwnData") {
        updateFilter({
          ...currentBoxFilter,
          ...(includeAccountsDropdown && { includeOwnData: !currentBoxFilter.includeOwnData }),
        });
        updateLocalFilter({
          ...currentBoxFilter,
          ...(includeAccountsDropdown && { includeOwnData: !currentBoxFilter.includeOwnData }),
        });
      }
      if (type === "accountsDropdown") {
        updateFilter({
          ...currentBoxFilter,
          ...(includeAccountsDropdown && { accounts: data }),
        });
        updateLocalFilter({
          ...currentBoxFilter,
          ...(includeAccountsDropdown && { accounts: data }),
        });
      }
      if (type === "isDistinct") {
        updateFilter({
          ...currentBoxFilter,
          isDistinct: data.value,
        });
        updateLocalFilter({
          ...currentBoxFilter,
          isDistinct: data.value,
        });
      }
      if (type === "type") {
        updateFilter({
          ...currentBoxFilter,
          type: data.value,
        });
        updateLocalFilter({
          ...currentBoxFilter,
          type: data.value,
        });
      }
      if (type === "priority") {
        updateFilter({
          ...currentBoxFilter,
          priority: data,
        });
        updateLocalFilter({
          ...currentBoxFilter,
          priority: data,
        });
      }
      if (type === "status") {
        updateFilter({
          ...currentBoxFilter,
          status: data,
        });
        updateLocalFilter({
          ...currentBoxFilter,
          status: data,
        });
      }
    },
    [updateFilter, updateLocalFilter, includeAccountsDropdown, currentBoxFilter],
  );

  return (
    <>
      <label>Date</label>
      <DropdownWrapper
        items={filterOptions}
        className={"flowPerformanceFilter__dropdown"}
        selection
        fluid
        onChange={(_, data) => handleChange(data, "date")}
        value={currentBoxFilter.date}
        trigger={
          <Tooltip
            inverted
            target={<TextTruncate>{filterOptions?.[SidebarFilterToFilter(currentBoxFilter.date)]?.text}</TextTruncate>}
            content={filterOptions?.[SidebarFilterToFilter(currentBoxFilter.date)]?.text}
          />
        }
      />
      {includeAccountsDropdown && (
        <>
          <label className={"accounts"}>{"Accounts"}</label>
          {/* @ts-ignore */}
          <FilterMultiSelectWithWindow
            propertyName={"accounts"}
            placeholder={"All Customer Accounts"}
            items={reportingAccountsWithSearch.items}
            filter={currentBoxFilter as any}
            updateFilter={(data: any) => handleChange(data.accounts, "accountsDropdown")}
            initialCheckedItems={[allCustomerAccounts]}
            searchPredicate={(item: Accounts, term: string) => item.text.toLowerCase().includes(term.toLowerCase())}
            // Temporary
            getDescription={(item: Accounts) => item?.text || ""}
            remoteSearch={currentBoxFilter.type !== "sp"}
            onRemoteSearchTermChange={setAccountsSearchTerm}
            fetchItems={reportingAccountsWithSearch.hasMore ? reportingAccountsWithSearch.loadMore : undefined}
            hasMore={reportingAccountsWithSearch.hasMore}
            isLoading={reportingAccountsWithSearch.isLoading || reportingAccountsWithSearch.isFetchingNext}
          />
          <Checkbox
            checked={currentBoxFilter.includeOwnData}
            onChange={(_, data) => handleChange(data, "includeOwnData")}
            label="Include My Account Data"
          />
        </>
      )}
      {includeAccountsDropdown && shouldSeeDropdown && (
        <div className="flowPerformanceFilter__reportType">
          <label className="type">Publisher</label>
          <DropdownWrapper
            items={[
              { text: `${accountName || "My Account"}`, value: "sp" },
              { text: "All Publishers", value: "account" },
            ]}
            className={"flowPerformanceFilter__dropdown publisher-dropdown"}
            selection
            fluid
            onChange={(_, data) => handleChange(data, "type")}
            value={currentBoxFilter.type}
          />
        </div>
      )}
      <div className={"flowPerformanceFilter__distinct"}>
        <label>
          Activity
          <Tooltip
            target={<Icon className="info circle" />}
            hideOnScroll
            showAlways
            content={`Select "Total" to include all user activities, even repeats. Choose "Distinct" to exclude repeated activities by the same user.`}
            position="top center"
            hoverable
          />
        </label>
        <DropdownWrapper
          items={mappedDistinct}
          selection
          fluid
          onChange={(_, data) => handleChange(data, "isDistinct")}
          value={currentBoxFilter.isDistinct}
        />
      </div>
      <div className={"flowPerformanceFilter__priorityLevels"}>
        <label>Priority Level</label>
        <FilterMultiSelect
          propertyName={"priority"}
          placeholder={"All Priority Levels"}
          items={mappedPriorityLevels}
          filter={currentBoxFilter}
          updateFilter={(data) => handleChange(data.priority, "priority")}
          search={customDropdownSearch}
        />
      </div>
      <div className={"flowPerformanceFilter__status"}>
        <label>Status</label>
        <FilterMultiSelect
          propertyName={"status"}
          placeholder={"All Statuses"}
          items={mappedStatusLevels}
          filter={currentBoxFilter}
          updateFilter={(data) => handleChange(data.status, "status")}
          search={customDropdownSearch}
        />
      </div>
    </>
  );
};
